/**
 * SoundBoxesSVG — Phase 1, Component 11 of the TeachBud Representation Catalog
 *
 * Renders Elkonin (sound) boxes per §5.2 Phonemic Awareness:
 *   - Row of 3–5 equal-sized squares, one per phoneme
 *   - Student version: empty boxes; student says each sound and pushes a counter
 *   - Teacher/example version: phoneme label inside each box
 *   - Word shown above the boxes (when provided)
 *   - Grades K–1 ELA
 */

import React from 'react';

export interface SoundBoxesProps {
  /** Number of phoneme boxes. Default 3. */
  phonemeCount?: number;
  /** Phonemes to show in each box (teacher mode). */
  phonemes?: string[];
  /** Word to display above the boxes. */
  word?: string;
  /** When true, boxes are empty — student fills in. */
  studentMode?: boolean;
  /** Override pixel width. */
  width?: number;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W   = 400;
const PAD    = 24;
const WORD_H = 48;
const BOX_H  = 90;
const BOX_GAP = 10;
const VB_H  = PAD + WORD_H + BOX_H + PAD;

const BOX_FILL    = '#FFFBEB';  // amber-50 (K–1 warmth)
const BOX_STROKE  = '#F59E0B';  // TeachBud amber
const WORD_COLOR  = '#1E3A5F';
const LABEL_COLOR = '#92400E';  // amber-800
const BLANK_TINT  = '#FEF3C7';  // amber-100

export function SoundBoxesSVG({
  phonemeCount = 3,
  phonemes = [],
  word,
  studentMode = true,
  width,
  className = '',
}: SoundBoxesProps) {
  const nBoxes = Math.max(2, Math.min(5, phonemeCount));
  const totalBoxW = VB_W - 2 * PAD;
  const singleBoxW = Math.floor((totalBoxW - (nBoxes - 1) * BOX_GAP) / nBoxes);

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label={word ? `Elkonin boxes for "${word}"` : `${nBoxes} sound boxes`}
    >
      <title>{word ? `Sound boxes: ${word}` : 'Elkonin boxes'}</title>

      {/* Word above boxes */}
      {word && (
        <>
          <text
            x={VB_W / 2} y={PAD + 28}
            textAnchor="middle"
            fontSize={28}
            fontFamily="Nunito, sans-serif"
            fontWeight={800}
            fill={WORD_COLOR}
            letterSpacing={2}
          >
            {word}
          </text>
          {/* Underline */}
          <line
            x1={VB_W / 2 - word.length * 9} y1={PAD + 34}
            x2={VB_W / 2 + word.length * 9} y2={PAD + 34}
            stroke={WORD_COLOR} strokeWidth={2}
          />
        </>
      )}

      {/* Phoneme boxes */}
      {Array.from({ length: nBoxes }).map((_, i) => {
        const bx = PAD + i * (singleBoxW + BOX_GAP);
        const by = PAD + WORD_H;
        const phoneme = !studentMode ? (phonemes[i] ?? null) : null;

        return (
          <g key={i}>
            {/* Shadow */}
            <rect x={bx + 2} y={by + 2} width={singleBoxW} height={BOX_H} rx={8} fill="#FDE68A" opacity={0.6} />
            {/* Box */}
            <rect
              x={bx} y={by}
              width={singleBoxW} height={BOX_H}
              rx={8}
              fill={studentMode ? BLANK_TINT : BOX_FILL}
              stroke={BOX_STROKE}
              strokeWidth={2.5}
            />

            {/* Phoneme label */}
            {phoneme && (
              <text
                x={bx + singleBoxW / 2}
                y={by + BOX_H / 2 + 12}
                textAnchor="middle"
                fontSize={32}
                fontFamily="Nunito, sans-serif"
                fontWeight={800}
                fill={LABEL_COLOR}
              >
                {phoneme}
              </text>
            )}

            {/* Student mode: pencil dot hint */}
            {studentMode && (
              <circle
                cx={bx + singleBoxW / 2}
                cy={by + BOX_H / 2}
                r={6}
                fill="#FDE68A"
                stroke={BOX_STROKE}
                strokeWidth={1.5}
                strokeDasharray="3 2"
              />
            )}
          </g>
        );
      })}

      {/* Box count label */}
      <text
        x={VB_W / 2} y={VB_H - 4}
        textAnchor="middle"
        fontSize={9}
        fontFamily="Inter, sans-serif"
        fontWeight={500}
        fill="#9CA3AF"
        letterSpacing={1}
      >
        {nBoxes} SOUND BOXES
      </text>
    </svg>
  );
}

export function parseSoundBoxesHint(hint: string): SoundBoxesProps {
  const countMatch = hint.match(/(\d+)\s*(sound|phoneme|box)/i);
  const wordMatch  = hint.match(/(?:boxes?|phonemes?) for "?([a-z]+)"?/i);
  return {
    phonemeCount: countMatch ? parseInt(countMatch[1]) : 3,
    word: wordMatch ? wordMatch[1] : undefined,
    studentMode: true,
  };
}
